/**
 * Compliance rule packs: working-time rules a restaurant writes for itself and
 * hangs on a site. Enterprise (`staff.compliance_advanced`).
 *
 * The templates are starting points, never law. Each carries a `note` saying
 * so, and a screen offering them should show it — the numbers in them are
 * round, and whether any of them matches the rules where a site trades is for
 * the restaurant to check.
 *
 * Rule values are minutes, except `max_days_in_a_row` (days) and the two
 * times of day, `earliest_start` and `latest_end` (minutes after midnight, in
 * the site's clock).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { ApiError } from './client';
import { useApi } from './provider';

export type PackRuleKind =
  | 'max_daily_minutes'
  | 'max_weekly_minutes'
  | 'min_rest_minutes'
  | 'break'
  | 'overtime_daily_minutes'
  | 'overtime_weekly_minutes'
  | 'earliest_start'
  | 'latest_end'
  | 'max_days_in_a_row';

export interface PackRule {
  kind: PackRuleKind;
  applies_to: 'everyone' | 'minors';
  value: number;
  /** The break rule only: how long a day is before the break is owed. */
  threshold: number | null;
  /** `warn` lets the shift or clock-in through and says so; `block` refuses it. */
  mode: 'warn' | 'block';
}

export interface CompliancePack {
  id: string;
  name: string;
  note: string | null;
  template_key: string | null;
  /** Younger than this is a young worker. Null: no age rules. */
  minor_age: number | null;
  rules: PackRule[];
  /** The sites measured against it. A pack in use cannot be deleted. */
  location_ids: string[];
}

export interface PackTemplate {
  key: string;
  name: string;
  description: string;
  /** "A starting point, not the law anywhere…" — show it. */
  note: string;
  minor_age: number | null;
  rules: PackRule[];
}

export function usePackTemplates(enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: ['staff', 'compliance-packs', 'templates'],
    queryFn: ({ signal }) =>
      api.get<PackTemplate[]>('/v1/staff/compliance-packs/templates', signal),
    enabled,
    staleTime: 3_600_000,
  });
}

export function useCompliancePacks(enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: ['staff', 'compliance-packs', 'list'],
    queryFn: ({ signal }) => api.get<CompliancePack[]>('/v1/staff/compliance-packs', signal),
    enabled,
    staleTime: 60_000,
  });
}

/** Which pack a site is on. `pack_id` null: none. */
export function useSitePack(locationId: string | undefined, enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: ['staff', 'compliance-packs', 'site', locationId ?? null],
    queryFn: ({ signal }) =>
      api.get<{ location_id: string; pack_id: string | null }>(
        `/v1/staff/compliance-packs/site?location_id=${locationId}`,
        signal,
      ),
    enabled: enabled && Boolean(locationId),
    staleTime: 60_000,
  });
}

function invalidate(queryClient: ReturnType<typeof useQueryClient>) {
  // The rota, the clock and the compliance report all answer to the pack.
  void queryClient.invalidateQueries({ queryKey: ['staff'] });
}

/** A new pack, copied from a starting point for the restaurant to edit. */
export function useCopyPackTemplate() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<CompliancePack, ApiError, { templateKey: string; name: string }>({
    mutationFn: ({ templateKey, name }) =>
      api.post<CompliancePack>('/v1/staff/compliance-packs', {
        template_key: templateKey,
        name,
      }),
    onSettled: () => invalidate(queryClient),
  });
}

/** Replace a pack's name, note, age and rules in one go. */
export function useSavePack() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<
    CompliancePack,
    ApiError,
    {
      packId: string;
      name: string;
      note: string | null;
      minorAge: number | null;
      rules: PackRule[];
    }
  >({
    mutationFn: ({ packId, name, note, minorAge, rules }) =>
      api.request<CompliancePack>(`/v1/staff/compliance-packs/${packId}`, {
        method: 'PUT',
        body: { name, note, minor_age: minorAge, rules },
      }),
    onSettled: () => invalidate(queryClient),
  });
}

/** Delete a pack. Refused with a 409 while a site is on it. */
export function useRemovePack() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<void, ApiError, { packId: string }>({
    mutationFn: ({ packId }) =>
      api.request<void>(`/v1/staff/compliance-packs/${packId}`, { method: 'DELETE' }),
    onSettled: () => invalidate(queryClient),
  });
}

/** Put a site on a pack, or take it off one (`packId` null). */
export function useAssignPack() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<
    { location_id: string; pack_id: string | null },
    ApiError,
    { locationId: string; packId: string | null }
  >({
    mutationFn: ({ locationId, packId }) =>
      api.request<{ location_id: string; pack_id: string | null }>(
        '/v1/staff/compliance-packs/site',
        { method: 'PUT', body: { location_id: locationId, pack_id: packId } },
      ),
    onSettled: () => invalidate(queryClient),
  });
}
